class ExponeaReco extends HTMLElement {
  static SESSION_STORAGE_KEY = 'exponeaCachedHandles';
  static OBSERVER_ROOT_MARGIN = '150px';
  static TIMEOUT_DURATION = 2000; // in ms
  // static WISELLI_LOCAL_STORAGE =
  //  'wiselli-70b9accb-be45-4ccf-b084-a45cf67add3d/3a7d9b16-5c40-4338-aa63-0e7e89709bb3_global-split';
  static WISELLI_VARIANT_TIMEOUT = 1000; // in ms

  constructor() {
    super();
    this.status = 'PENDING'; // PENDING, LOADED, TIMED_OUT
    this.abTestVariant = false;
  }

  connectedCallback() {
    this.productsContainer = this.querySelector('.js-container-for-products');
    this.productsToShow = this.getAttribute('products-to-show') || 10;
    this.recommendationId = this.getAttribute('recommendation-id');
    this.recommendationAbId = this.getAttribute('recommendation-ab-id');
    this.recommendationAbVariantWeight = this.getAttribute('recommendation-ab-variant-weight');
    this.recommendationContextSku = this.getAttribute('recommendation-context-sku') || '';
    this.cachedHandles = [];
    this.fallbackHandles = this.getAttribute('fallback-handles')?.split('+') || [];
    this.excludedHandles = this.getAttribute('excluded-handles')?.split('+') || [];
    this.placement = this.getAttribute('recommendation-placement') || '';

    this.updateCachedHandles();
    if (this.status == 'PENDING') {
      this._preloadObserver.observe(this);
    }
    this.productsContainer.addEventListener('click', this._handleRecommendationsClick);
  }

  disconnectedCallback() {
    if (this._timeout) {
      clearTimeout(this._timeout);
    }
    if (this._preloadObserver) {
      this._preloadObserver.disconnect();
    }
    this.productsContainer.removeEventListener('click', this._handleRecommendationsClick);
  }

  updateCachedHandles() {
    const sessionData = sessionStorage.getItem(ExponeaReco.SESSION_STORAGE_KEY);
    if (sessionData) {
      this.cachedHandles = sessionData.split('+').map((item) => encodeURIComponent(item));
    }
  }

  _preloadObserver = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        this.classList.add('is-observed');
        this._load();
        this._preloadObserver.disconnect();
      }
    },
    { rootMargin: ExponeaReco.OBSERVER_ROOT_MARGIN },
  );

  // main component logic
  async _load() {
    try {
      if (window.isExponeaDisabled) {
        // console.log('Exponea is disabled. Showing fallback recommendations.');
        this._fetchAndRenderProducts([...this.cachedHandles, ...this.fallbackHandles]);
        return;
      }

      // check if there wiselli settings stored in localStorage for this reco
      const shopifySection = this.closest('[id^="shopify-section"]');
      if (shopifySection) {
        const wiselliKey = `${shopifySection.id}_reco`;
        const wiselliRecoSettings = JSON.parse(localStorage.getItem(wiselliKey));
        if (wiselliRecoSettings) {
          const wiselliRecoId = wiselliRecoSettings['recommendation-id'];
          const wiselliVariant = wiselliRecoSettings['wiselli-variant'];
          const expirationTimestamp = wiselliRecoSettings['expirationTimestamp'];
          const dateNow = Date.now();

          if (wiselliVariant && expirationTimestamp && dateNow < expirationTimestamp) {
            console.log('load reco from wiselli cache');
            this.abTestVariant = 'WiselliTarget';
            if (wiselliRecoId) {
              this.setAttribute('recommendation-id', wiselliRecoId);
              this.recommendationId = wiselliRecoId;
            }
            this.setAttribute('wiselli-variant', wiselliVariant);
            this.abTestVariantWiselli = wiselliVariant;
            this._loadRecommendations();
            return;
          }
        }
      }
      this.abTestVariant = await this._getAbTestVariant();

      if (this.abTestVariant === 'WiselliTarget') {
        console.log('load reco for wiselli target');
        // static timeout to make sure Wiselli script updated the reco ID
        setTimeout(() => {
          this.recommendationId = this.getAttribute('recommendation-id');
          this.abTestVariantWiselli = this.getAttribute('wiselli-variant') || 'wiselli-timeout';
          this._loadRecommendations();
        }, ExponeaReco.WISELLI_VARIANT_TIMEOUT);
      } else {
        console.log('load reco for controll group');
        this._loadRecommendations();
      }
    } catch (error) {
      console.error('Failed to load recommendations due to an error:' + error + '. Fallback shown.');
      this._fetchAndRenderProducts(this.fallbackHandles);
    }
  }
  // A/B test handling exponea split for Wiselli test, if configured for reco component
  _getAbTestVariant() {
    if (this.recommendationAbId === 'WiselliGlobalStandalone') {
      const testName = this.recommendationAbId;
      const variantWeight = {
        WiselliTarget: Number(this.recommendationAbVariantWeight),
        WiselliControl: 100 - Number(this.recommendationAbVariantWeight),
      };

      return new Promise((resolve) => {
        window.exponea.getAbTest(testName, variantWeight, (variant) => {
          resolve(variant);
        });
      });
    }
    return Promise.resolve(false);
  }

  _loadRecommendations() {
    const options = {
      recommendationId: this.recommendationId,
      size: Number(this.productsToShow),
      callback: this._handleRecommendationsLoaded.bind(this),
      fillWithRandom: true,
      catalogFilter: [],
      catalogAttributesWhitelist: ['HANDLE', 'ITEM_GROUP_ID'],
      items: {},
    };
    if (this.recommendationContextSku) {
      options.items[this.recommendationContextSku] = 1;
    }
    if (window.exponea && typeof window.exponea.getRecommendation === 'function') {
      window.exponea.getRecommendation(options);
    }

    this._timeout = setTimeout(() => {
      if (this.status === 'LOADED') return;
      this.status = 'TIMED_OUT';
      this._trackRecommendation({ action: 'timeout' });
      this._fetchAndRenderProducts([...this.cachedHandles, ...this.fallbackHandles]);
    }, ExponeaReco.TIMEOUT_DURATION);
  }

  _handleRecommendationsLoaded(data) {
    if (this.status === 'TIMED_OUT') return;
    this.status = 'LOADED';
    clearTimeout(this._timeout);

    let options = {};
    if (!data || data.length === 0) {
      options = {
        action: 'show',
        item_ids: [],
        note: 'no recommendations provided, fallback shown',
      };
      this._trackRecommendation(options);
      this._fetchAndRenderProducts([...this.cachedHandles, ...this.fallbackHandles]);
    } else {
      const recommendedHandles = data.map((item) => encodeURIComponent(item.HANDLE));
      options = {
        action: 'show',
        item_ids: data.map((item) => item.ITEM_GROUP_ID),
      };

      this._fetchAndRenderProducts([...recommendedHandles, ...this.cachedHandles, ...this.fallbackHandles]);
      this._trackRecommendation(options);
    }
  }

  async _fetchAndRenderProducts(handles) {
    if (!handles || handles.length === 0) return;
    if (!this.productsContainer) return;

    try {
      const uniqueHandlesSet = new Set(handles);
      this.excludedHandles.forEach((handle) => uniqueHandlesSet.delete(handle));
      handles = [...uniqueHandlesSet];
      const campaign_code = new URLSearchParams(window.location.search).get('code');
      const baseUrl = window.routes.all_products_collection_url || '/collections/all';
      const url =
        `${baseUrl}?view=list-products&limit=${this.productsToShow}&type=${handles.join('+')}` +
        (campaign_code ? `&q=${campaign_code}` : '');
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Network response was not ok: ${response.statusText}`);

      this._renderHtml(await response.text());
      this._updateSlider();
    } catch (error) {
      console.error('Fetch or render error:', error);
    }
  }

  _renderHtml(htmlText) {
    const parser = new DOMParser();
    const parsedHTML = parser.parseFromString(htmlText, 'text/html');
    const parsedElements = parsedHTML.querySelectorAll('.js-fetched-item');
    const fragment = document.createDocumentFragment();
    parsedElements.forEach((element, index) => {
      element.setAttribute('id', `Slide-prlist-${index + 1}`);
      element.classList.add('slider__slide');
      fragment.appendChild(element);
    });
    this.productsContainer.innerHTML = '';
    window.updateWishlistStateImmediately?.(fragment);
    this.productsContainer.appendChild(fragment);
  }
  _handleRecommendationsClick = (event) => {
    const anchorElement = event.target.closest('a');
    if (anchorElement) {
      this._trackRecommendation({ action: 'click' });
    }
  };
  _updateSlider() {
    const keenSlider = this.productsContainer.closest('keen-slider-component');
    if (keenSlider && keenSlider.dataset.sliderAutoplayOnFetch === 'true') {
      keenSlider.play();
    }
  }

  _trackRecommendation(properties = {}) {
    if (this.abTestVariant) {
      properties.variant = this.abTestVariant;
    }
    if (this.abTestVariantWiselli) {
      properties.variant_wiselli = this.abTestVariantWiselli;
    }

    if (typeof exponeaTrackSafe === 'function')
      exponeaTrackSafe('recommendation', {
        recommendation_id: this.recommendationId,
        placement: this.placement,
        ...properties,
      });
  }
}

customElements.define('exponea-reco', ExponeaReco);

function handleGlobalCachedRecommendations(data) {
  const SESSION_STORAGE_KEY = 'exponeaCachedHandles';
  const recommendationId = window.themeSettings?.exponea_reco_id_for_cache;
  if (data && data.length > 0) {
    const handles = data.map((item) => item.HANDLE);
    sessionStorage.setItem(SESSION_STORAGE_KEY, handles.join('+'));
    if (typeof exponeaTrackSafe === 'function') {
      exponeaTrackSafe('recommendation', {
        action: 'cached',
        recommendation_id: recommendationId,
      });
    }
    document.querySelectorAll('exponea-reco').forEach((component) => {
      if (typeof component.updateCachedHandles === 'function') {
        component.updateCachedHandles();
      }
    });
  } else {
    console.log('Exponea: No handles received to cache.');
  }
}

function initExponeaCache() {
  if (sessionStorage.getItem('exponeaCachedHandles')) {
    return;
  }
  const recommendationId = window.themeSettings?.exponea_reco_id_for_cache;
  const productsQty = parseInt(window.themeSettings?.exponea_reco_cached_qty) || 10;

  if (window.exponea && typeof window.exponea.getRecommendation === 'function' && recommendationId) {
    const options = {
      recommendationId: recommendationId,
      size: productsQty,
      callback: handleGlobalCachedRecommendations,
      fillWithRandom: true,
      catalogAttributesWhitelist: ['HANDLE'],
    };

    window.exponea.getRecommendation(options);
  }
}

initExponeaCache();
